import React, { useEffect, createElement } from 'react';
interface SEOHeadProps {
  title?: string;
  description?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  keywords?: string;
  themeColor?: string;
  appName?: string;
  jsonLd?: Record<string, unknown>;
}
export function SEOHead({
  title = 'Pond — Less noise. More you. | Mindful Distraction Blocker for iOS',
  description = 'Pond gently blocks distractions so you can be present for the moments that matter. Rated 4.8★ on the App Store. Download the mindful screen time app by PIXO Inc.',
  canonicalUrl = 'https://pond.app',
  ogImage = 'https://cdn.magicpatterns.com/uploads/6X5Ni2Q9nDpaafqtoDsFw2/Rectangle_2404.png',
  ogType = 'website',
  twitterCard = 'summary_large_image',
  keywords = 'distraction blocker, screen time, mindfulness, focus app, digital wellbeing, iOS app, phone addiction, be present, PIXO, Pond app',
  themeColor = '#0F1A2E',
  appName = 'Pond',
  jsonLd
}: SEOHeadProps) {
  useEffect(() => {
    // Title
    document.title = title;
    // Helper to set or create a meta tag
    const setMeta = (attr: string, key: string, content: string) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    // Standard meta tags
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'author', 'PIXO Inc.');
    setMeta('name', 'robots', 'index, follow, max-image-preview:large, max-snippet:-1');
    setMeta('name', 'theme-color', themeColor);
    setMeta('name', 'application-name', appName);
    setMeta('name', 'apple-mobile-web-app-title', appName);
    setMeta('name', 'apple-mobile-web-app-capable', 'yes');
    setMeta('name', 'apple-mobile-web-app-status-bar-style', 'black-translucent');
    setMeta('name', 'mobile-web-app-capable', 'yes');
    // Open Graph
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:image:alt', 'Pond App — Mindful distraction blocker for iOS');
    setMeta('property', 'og:image:width', '1200');
    setMeta('property', 'og:image:height', '630');
    setMeta('property', 'og:site_name', 'Pond');
    setMeta('property', 'og:locale', 'en_US');
    // Twitter Card
    setMeta('name', 'twitter:card', twitterCard);
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);
    setMeta('name', 'twitter:image:alt', 'Pond App — Mindful distraction blocker for iOS');
    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);
    // JSON-LD Structured Data
    const defaultJsonLd = jsonLd || {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'Pond',
      description: 'Pond gently blocks distractions so you can be present for the moments that matter.',
      applicationCategory: 'LifestyleApplication',
      operatingSystem: 'iOS',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD'
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        ratingCount: '500',
        bestRating: '5',
        worstRating: '1'
      },
      author: {
        '@type': 'Organization',
        name: 'PIXO Inc.',
        url: 'https://pixo.co'
      },
      url: canonicalUrl,
      image: ogImage
    };
    let scriptEl = document.querySelector('script[data-seo="jsonld"]') as HTMLScriptElement | null;
    if (!scriptEl) {
      scriptEl = document.createElement('script');
      scriptEl.setAttribute('type', 'application/ld+json');
      scriptEl.setAttribute('data-seo', 'jsonld');
      document.head.appendChild(scriptEl);
    }
    scriptEl.textContent = JSON.stringify(defaultJsonLd);
    // Cleanup on unmount
    return () => {
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, canonicalUrl, ogImage, ogType, twitterCard, keywords, themeColor, appName, jsonLd]);
  return null;
}